import language from "i18next";
import { getParsedClustersResources } from "./create/form";
import Validator from "services/validator";
import {
  isValidRegexNamespace,
  isNamespaceAllocationValid,
  isValidNamespaceGPUAllocation,
  isValidGPUAllocation,
  isWorkspaceQuotaExceeded,
  isClusterAllocationValid,
  ApplyIf,
  isValidClusterGPUAllocation,
  Missing,
} from "services/validator/rules";

export const workspaceNamespaceValidator = new Validator();
workspaceNamespaceValidator.addRule(["namespaceName"], Missing());
workspaceNamespaceValidator.addRule("namespaceName", isValidRegexNamespace());
workspaceNamespaceValidator.addRule(
  ["alocCpu", "alocMemory"],
  isNamespaceAllocationValid()
);

workspaceNamespaceValidator.addRule(["gpuConfig"], isValidGPUAllocation());
const gpuConfigValidator = new Validator();
gpuConfigValidator.addRule(["limit"], isValidNamespaceGPUAllocation());
gpuConfigValidator.addRule(
  ["limit"],
  ApplyIf(
    (value, key, data) => data.provider && data.limit === "",
    () => language.t("Please provide GPU value if vendor is selected")
  )
);
gpuConfigValidator.addRule(
  ["provider"],
  ApplyIf(
    (value, key, data) => data.limit !== "" && !data.provider,
    () =>
      language.t("Please select GPU vendor when GPU allocation is specified")
  )
);
workspaceNamespaceValidator.addRule("gpuConfig", gpuConfigValidator);
workspaceNamespaceValidator.addRule(["namespaces"], isClusterAllocationValid());
workspaceNamespaceValidator.addRule(
  ["namespaces"],
  isValidClusterGPUAllocation()
);
workspaceNamespaceValidator.addRule("namespaces", isClusterCPULimitExceeded);
workspaceNamespaceValidator.addRule("namespaces", isClusterMemoryLimitExceeded);
workspaceNamespaceValidator.addRule("namespaces", isClusterGPULimitExceeded);
workspaceNamespaceValidator.addRule("namespaces", isWorkspaceQuotaExceeded());
workspaceNamespaceValidator.addRule("namespaces", isUnlimitedAloc);
workspaceNamespaceValidator.addRule("namespaces", areResourcesConsistent());
workspaceNamespaceValidator.addRule("namespaceName", (value, key, data) => {
  if (
    data?.namespaces?.find(
      (ns, index) => ns.namespaceName === value && index !== data.selectedIndex
    )
  ) {
    return language.t("Namespaces must be unique");
  }
  return false;
});

export function* isUnlimitedAloc(value, key, data) {
  const selectedIndex = data?.selectedIndex;
  const namespaces = data.namespaces || [];

  const hasQuota =
    parseFloat(data.cpuQuota) > 0 ||
    parseFloat(data.memoryQuota) > 0 ||
    parseInt(data.gpuQuota) > 0;
  const targetIndex =
    selectedIndex >= 0 ? selectedIndex : namespaces.length - 1;

  for (const childIndex in namespaces[targetIndex]?.children) {
    const isAlocCpuNegative =
      namespaces[targetIndex]?.children[childIndex]?.alocCpu < 0;

    const isAlocMemoryNegative =
      namespaces[targetIndex]?.children[childIndex]?.alocMemory < 0;

    const isAlocGpuNegative =
      namespaces[targetIndex]?.children[childIndex]?.gpuConfig?.limit < 0;

    const message = language.t(
      "Cluster can't have unlimited allocation due to workspace {{aloc}} quota",
      {
        aloc:
          parseFloat(data.cpuQuota) > 0
            ? "CPU"
            : parseFloat(data.memoryQuota) > 0
            ? "Memory"
            : "GPU",
      }
    );

    yield {
      field: `namespaces.${targetIndex}.children.${childIndex}.alocCpu`,
      result: hasQuota && isAlocCpuNegative ? message : false,
    };
    yield {
      field: `namespaces.${targetIndex}.children.${childIndex}.alocMemory`,
      result: hasQuota && isAlocMemoryNegative ? message : false,
    };
    yield {
      field: `namespaces.${targetIndex}.children.${childIndex}.gpuConfig.limit`,
      result: hasQuota && isAlocGpuNegative ? message : false,
    };
  }
}

export function* isClusterCPULimitExceeded(value, key, data) {
  const selectedIndex = data?.selectedIndex;
  const namespaces = data.namespaces || [];

  const parsedClustersResourcesData = getParsedClustersResources(data);
  const parsedCpuQuota = parseFloat(data?.cpuQuota || 0);

  const higherCpuThanQuotaClusters = parsedClustersResourcesData.filter(
    (cluster) => parsedCpuQuota > 0 && cluster.totalAlocCpu > parsedCpuQuota
  );

  const targetIndex =
    selectedIndex >= 0 ? selectedIndex : namespaces.length - 1;

  for (const childIndex in namespaces[targetIndex]?.children) {
    yield {
      field: `namespaces.${targetIndex}.children.${childIndex}.alocCpu`,
      result:
        parseInt(childIndex) === higherCpuThanQuotaClusters?.[0]?.index
          ? language.t(
              "The CPU allocated for this cluster has exceeded the workspace quota"
            )
          : false,
    };
  }
}

export function* isClusterMemoryLimitExceeded(value, key, data) {
  const selectedIndex = data?.selectedIndex;
  const namespaces = data.namespaces || [];

  const parsedClustersResourcesData = getParsedClustersResources(data);
  const parsedMemoryQuota = parseFloat(data?.memoryQuota || 0);

  const higherMemoryThanQuotaClusters = parsedClustersResourcesData.filter(
    (cluster) =>
      parsedMemoryQuota > 0 && cluster.totalAlocMemory > parsedMemoryQuota
  );

  const targetIndex =
    selectedIndex >= 0 ? selectedIndex : namespaces.length - 1;

  for (const childIndex in namespaces[targetIndex]?.children) {
    yield {
      field: `namespaces.${targetIndex}.children.${childIndex}.alocMemory`,
      result:
        parseInt(childIndex) === higherMemoryThanQuotaClusters?.[0]?.index
          ? language.t(
              "The memory allocated for this cluster has exceeded the workspace quota"
            )
          : false,
    };
  }
}

export function* isClusterGPULimitExceeded(value, key, data) {
  const selectedIndex = data?.selectedIndex;
  const namespaces = data.namespaces || [];

  const parsedClustersResourcesData = getParsedClustersResources(data);
  const parsedGpuQuota = parseFloat(data?.gpuQuota || 0);

  const higherGpuThanQuotaClusters = parsedClustersResourcesData.filter(
    (cluster) => parsedGpuQuota > 0 && cluster.totalAlocGpu > parsedGpuQuota
  );

  const targetIndex =
    selectedIndex >= 0 ? selectedIndex : namespaces.length - 1;

  for (const childIndex in namespaces[targetIndex]?.children) {
    yield {
      field: `namespaces.${targetIndex}.children.${childIndex}.gpuConfig.limit`,
      result:
        parseInt(childIndex) === higherGpuThanQuotaClusters?.[0]?.index
          ? language.t(
              "The GPU allocated for this cluster has exceeded the workspace quota"
            )
          : false,
    };
  }
}

export function areResourcesConsistent({
  message = () =>
    language.t(
      "Either all cluster resources (cpu, memory, gpu) quota should be 0/-1 or must be greater than 0"
    ),
} = {}) {
  return (value, key, data) => {
    const selectedIndex = data?.selectedIndex;
    const namespaces = data.namespaces || [];
    const targetIndex =
      selectedIndex >= 0 ? selectedIndex : namespaces.length - 1;

    const hasError = (namespaces[targetIndex]?.children || []).some(
      (cluster) => {
        const { alocCpu, alocMemory, gpuConfig } = cluster;
        const cpu = parseFloat(alocCpu) || 0;
        const memory = parseFloat(alocMemory) || 0;
        const gpu = parseInt(gpuConfig?.limit) || 0;

        const resources = [cpu, memory, gpu];
        const hasUnlimited = resources.some((val) => val === -1);
        const allUnlimited = resources.every((val) => val === -1);

        if (hasUnlimited && !allUnlimited) {
          return true;
        }

        return false;
      }
    );

    if (hasError) {
      return message();
    }
  };
}
